import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { API } from '../services';
import { ErrorMessageContext } from './ErrorMessageContext';
import debounce from 'lodash/debounce';
import { HYPHEN } from '../utilities/constants';
import { ApiLinksContext } from './ApiLinksContext';
import { getDriverNumber, getErrorMessage } from '../utilities/commonFunctions';

const DriverMonitoringSystemContext = createContext(null);

export const DriverMonitoringSystemProvider = ({ children }) => {
  const { addErrorMessage } = useContext(ErrorMessageContext);
  const [drowsinessTripsList, setDrowsinessTripsList] = useState([]);
  const [searchTextStickerId, setSearchTextStickerId] = useState('');
  const [searchTextTripId, setSearchTextTripId] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 12;
  const pollingTimerRef = useRef(null);
  const [activeTab, setActiveTab] = useState('true_dms_drowsy');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [selectedAlertFromMap, setSelectedAlertFromMap] = useState(null);
  const [showOpenTicketsOnly, setShowOpenTicketsOnly] = useState(false);
  const { apiLinks } = useContext(ApiLinksContext);
  const [lastUpdatedTime, setLastUpdatedTime] = useState(Date.now());

  const debouncedStickerIdSearch = useCallback(
    debounce(value => {
      setSearchTextStickerId(value);
      setPage(1);
    }, 500),
    []
  );

  const debouncedTripIdSearch = useCallback(
    debounce(value => {
      setSearchTextTripId(value);
      setPage(1);
    }, 500),
    []
  );

  const handleShowOpenTicketsOnly = () => {
    setShowOpenTicketsOnly(prev => !prev);
    setPage(1);
  };

  const fetchDriverDrowsinessData = async (pageNum = 1, isPolling = false) => {
    try {
      setLoading(true);
      const { data } = await API.getDriverDrowsinessData(
        {
          alert_filter: activeTab,
          ticket_status: showOpenTicketsOnly ? 'open' : 'all',
          ...(searchTextStickerId !== '' && {
            sticker_id: searchTextStickerId,
          }),
          ...(searchTextTripId !== '' && { trip_id: searchTextTripId }),
        },
        pageNum,
        PAGE_SIZE,
        apiLinks
      );

      if (data) {
        const formattedData = data?.results?.map((event, index) => ({
          key: `${pageNum}-${index}`,
          consolidated_ticket_status:
            event?.consolidated_ticket_status || HYPHEN,
          latest_dms_timestamp: event?.latest_dms_timestamp || '',
          latest_video_url: event?.latest_video_url || '',
          trip_id: event?.trip_id || HYPHEN,
          vehicle_number: event?.vehicle_number || HYPHEN,
          device_id: event?.device_id || HYPHEN,
          total_alerts: event?.total_alerts || 0,
          unread_alerts: event?.unread_alerts,
          sticker_id: event?.sticker_id || HYPHEN,
          comment: event?.comment,
          latest_action_taken: event?.latest_action_taken,
          latest_action_taken_by: event?.latest_action_taken_by || HYPHEN,
          driver_name: event?.driver_name || HYPHEN,
          driver_number: getDriverNumber(event?.driver_number),
        }));

        if (pageNum === 1) {
          setDrowsinessTripsList(formattedData);
        } else {
          setDrowsinessTripsList(prev => [...prev, ...formattedData]);
        }

        setPage(data?.next_page_number);
        setHasMore(data?.next_page_number !== null);
        setLastUpdatedTime(Date.now());
      }
    } catch (error) {
      addErrorMessage(
        getErrorMessage(
          error,
          'Unable to fetch Driver Drowsiness events! Please try again after sometime.'
        )
      );
      if (!isPolling) {
        setDrowsinessTripsList([]);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchDriverDrowsinessData(page);
    }
  };

  // Reset all filters/search to default
  const resetFiltersAndFetch = useCallback(() => {
    setSearchTextStickerId('');
    setSearchTextTripId('');
    setActiveTab('true_dms_drowsy');
    setShowOpenTicketsOnly(false);
    setPage(1);
  }, []);

  const contextValue = useMemo(
    () => ({
      drowsinessTripsList,
      debouncedStickerIdSearch,
      debouncedTripIdSearch,
      handleLoadMore,
      hasMore,
      pollingTimerRef,
      loading,
      searchTextStickerId,
      searchTextTripId,
      fetchDriverDrowsinessData,
      activeTab,
      setActiveTab,
      setSearchTextStickerId,
      setSearchTextTripId,
      setDrowsinessTripsList,
      isModalOpen,
      setIsModalOpen,
      selectedAlertFromMap,
      setSelectedAlertFromMap,
      selectedAlert,
      setSelectedAlert,
      showOpenTicketsOnly,
      handleShowOpenTicketsOnly,
      lastUpdatedTime,
      resetFiltersAndFetch,
    }),
    [
      drowsinessTripsList,
      searchTextStickerId,
      searchTextTripId,
      activeTab,
      isModalOpen,
      selectedAlert,
      showOpenTicketsOnly,
      lastUpdatedTime,
      resetFiltersAndFetch,
    ]
  );

  return (
    <DriverMonitoringSystemContext.Provider value={contextValue}>
      {children}
    </DriverMonitoringSystemContext.Provider>
  );
};

DriverMonitoringSystemProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useDriverMonitoringSystem = () =>
  useContext(DriverMonitoringSystemContext);
